/* File: src/components/guest/RegistrationForm.js */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../common/Input';
import Button from '../common/Button';
import { publicApi } from '../../services/api';
import { useEvent } from '../../contexts/EventContext';
import './RegistrationForm.css';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { eventDetails } = useEvent();
  const [loading, setLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  
  // Récupérer les données d'acquisition
  const source = localStorage.getItem('acquisition_source') || 'direct';
  const invitation = localStorage.getItem('invitation_type') || 'guinness';
  const referrer = localStorage.getItem('referrer') || '';
  
  // Vérifier si l'utilisateur a passé la vérification d'âge
  useEffect(() => {
    const birthYear = localStorage.getItem('birthYear');
    if (!birthYear) {
      navigate('/');
    }
  }, [navigate]);
  
  // Configuration de Formik pour gérer le formulaire
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      gender: '',
      district: '', // Optionnel
      acceptTerms: false
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required('Votre prénom est requis')
        .min(2, 'Votre prénom doit contenir au moins 2 caractères')
        .max(50, 'Votre prénom ne peut pas dépasser 50 caractères'),
      lastName: Yup.string()
        .required('Votre nom est requis')
        .min(2, 'Votre nom doit contenir au moins 2 caractères')
        .max(50, 'Votre nom ne peut pas dépasser 50 caractères'),
      phone: Yup.string()
        .required('Votre numéro de téléphone est requis')
        .matches(/^6[5-9][0-9]{7}$/, 'Veuillez entrer un numéro de téléphone camerounais valide (6xx xx xx xx)'),
      gender: Yup.string()
        .required('Veuillez sélectionner votre genre'),
      district: Yup.string(), // Optionnel, pas de .required()
      acceptTerms: Yup.boolean()
        .oneOf([true], 'Vous devez accepter les conditions pour continuer')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setSubmissionError('');
      
      try {
        // Récupérer les données de date de naissance complètes du localStorage
        const birthDay = localStorage.getItem('birthDay');
        const birthMonth = localStorage.getItem('birthMonth');
        const birthYear = localStorage.getItem('birthYear');
        
        // S'assurer que toutes les données sont présentes
        if (!birthDay || !birthMonth || !birthYear) {
          setSubmissionError('Informations d\'âge incomplètes. Veuillez retourner à la vérification d\'âge.');
          setLoading(false);
          return;
        }
        
        // Récupérer l'ID de l'événement de plusieurs sources possibles
        let eventId = localStorage.getItem('currentEventId');
        
        // Si pas d'ID dans localStorage, essayer de le récupérer du contexte
        if (!eventId && eventDetails) {
          if (eventDetails.id) {
            eventId = eventDetails.id;
          } else if (eventDetails.event_id) {
            // Certains APIs renvoient event_id au lieu de id
            eventId = eventDetails.event_id;
          }
        }
        
        // Utiliser '1' comme valeur par défaut si aucun ID n'est trouvé
        eventId = eventId || '1';
        
        // Vérifier que l'eventId est bien un entier
        eventId = parseInt(eventId, 10);
        if (isNaN(eventId)) {
          eventId = 1; // Valeur par défaut si la conversion échoue
        }
        
        console.log("Inscription à l'événement ID:", eventId);
        
        // Créer un objet avec la structure attendue par l'API
        const registrationData = {
          first_name: values.firstName,
          last_name: values.lastName,
          phone: values.phone,
          gender: values.gender,
          district: values.district || '',
          birthDay: parseInt(birthDay),
          birthMonth: parseInt(birthMonth),
          birthYear: parseInt(birthYear),
          source,
          invitation,
          referrer,
          event_id: eventId // Utiliser event_id pour correspondre à la structure de la table tickets
        };
        
        // Soumettre le formulaire avec les données correctement formatées
        const response = await publicApi.register(registrationData);
        
        if (response.data.success) {
          navigate(`/ticket/${values.phone}`);
        } else {
          setSubmissionError(response.data.message || 'Une erreur est survenue. Veuillez réessayer.');
        }
      } catch (error) {
        console.error('Registration error:', error);
        
        if (error.response && error.response.data && error.response.data.message) {
          setSubmissionError(error.response.data.message);
        } else {
          setSubmissionError('Une erreur est survenue. Veuillez réessayer plus tard.');
        }
      } finally {
        setLoading(false);
      }
    }
  });

  const handleBack = () => {
    navigate('/event');
  };
  
  return (
    <div className="registration-container">
      <div className="back-arrow" onClick={handleBack}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      
      <div className="registration-header">
        <img src="https://guinnessevents.cm/brightbar/assets/images/guinness-logo.png" alt="Guinness Bright Bar" className="registration-logo" />
        <h1>GUINNESS BRIGHT BAR</h1>
      </div>
      
      <div className="registration-form-container">
        <form onSubmit={formik.handleSubmit} className="registration-form">
          <div className="form-field">
            <label htmlFor="firstName" className="field-label">Prénom</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              className={`field-input ${formik.touched.firstName && formik.errors.firstName ? 'field-error-input' : ''}`}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Votre prénom"
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <div className="field-error">{formik.errors.firstName}</div>
            )}
          </div>
          
          <div className="form-field">
            <label htmlFor="lastName" className="field-label">Nom</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              className={`field-input ${formik.touched.lastName && formik.errors.lastName ? 'field-error-input' : ''}`}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Votre nom"
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <div className="field-error">{formik.errors.lastName}</div>
            )}
          </div>
          
          <div className="form-field">
            <label htmlFor="phone" className="field-label">Numéro de téléphone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className={`field-input ${formik.touched.phone && formik.errors.phone ? 'field-error-input' : ''}`}
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="6XX XX XX XX"
            />
            {formik.touched.phone && formik.errors.phone && (
              <div className="field-error">{formik.errors.phone}</div>
            )}
          </div>
          
          <div className="form-field">
            <label className="field-label">Genre</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={formik.values.gender === 'male'}
                  onChange={formik.handleChange}
                />
                <span className="radio-text">Homme</span>
              </label>
              
              <label className="radio-option">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={formik.values.gender === 'female'}
                  onChange={formik.handleChange}
                />
                <span className="radio-text">Femme</span>
              </label>
            </div>
            {formik.touched.gender && formik.errors.gender && (
              <div className="field-error">{formik.errors.gender}</div>
            )}
          </div>
          
          <div className="form-field">
            <label htmlFor="district" className="field-label">Quartier (facultatif)</label>
            <input
              type="text"
              id="district"
              name="district"
              className="field-input"
              value={formik.values.district}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Votre quartier"
            />
          </div>
          
          <div className="terms-container">
            <label className="terms-label">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formik.values.acceptTerms}
                onChange={formik.handleChange}
              />
              <span className="terms-text">
                J'accepte de recevoir des communications de Guinness Cameroun et je confirme avoir au moins 21 ans.
              </span>
            </label>
            
            {formik.touched.acceptTerms && formik.errors.acceptTerms && (
              <div className="terms-error">{formik.errors.acceptTerms}</div>
            )}
          </div>
          
          {submissionError && (
            <div className="submission-error">{submissionError}</div>
          )}
          
          <button
            type="submit"
            disabled={loading || !formik.isValid}
          >
            {loading ? 'INSCRIPTION EN COURS...' : 'OBTENIR MON PASS'}
          </button>
        </form>
      </div>
      
      <div className="registration-footer">
        <p>
          La consommation d'alcool est réservée aux personnes âgées de plus de 21 ans. 
          L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
        </p>
      </div>
    </div>
  );
};

export default RegistrationForm;